import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import jsPDF from 'jspdf';
import './App.css';
import checkMarkIcon from './assets/images/check-mark.svg';
import Logo from './assets/images/logo.svg';
import FileUpload from './components/FileUpload';
import UploadingIndicator from './components/UploadingIndicator';
import { AGB_URL, PRIVACY_URL } from './constants/links';
import CookieBanner from './components/CookieBanner';
import CookieSettingsModal from './components/CookieSettingsModal';
import { needsConsent, hasMarketingConsent } from './utils/cookieConsent';
import { useGoogleAds } from './utils/googleAds';
import { getOpenAiAttributionCookies, measureLeadCreated, useOpenAiPixel } from './utils/openaiPixel';

// Brunhilde API. The key is the scoped public submission key
// (BRUNHILD_PUBLIC_SUBMISSION_API_KEY): it can only create document submissions
// and request upload URLs for them.
const API_BASE_URL = 'https://8vzpkktxyi.execute-api.eu-central-1.amazonaws.com/prod';
const API_KEY = 'Ypfrw6eSgJV5Uvc8amyXlO2BmUd4rPSLilJdQq0LX-0';

function App() {
  const [submitterName, setSubmitterName] = useState('');
  const [submitterCompany, setSubmitterCompany] = useState('');
  const [submitterPhone, setSubmitterPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consentToTou, setConsentToTou] = useState(false);
  const [consentToPp, setConsentToPp] = useState(false);
  const [consentToEmailMarketing, setConsentToEmailMarketing] = useState(false);
  const [files, setFiles] = useState([]);
  const [extraInfo, setExtraInfo] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // null, 'success', or 'error'
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  // Initialize Google Ads gtag / OpenAI pixel based on marketing consent
  useGoogleAds();
  useOpenAiPixel();

  useEffect(() => {
    if (needsConsent()) {
      setShowCookieBanner(true);
    }

    const handleOpenCookieSettings = () => {
      setShowCookieSettings(true);
    };

    window.addEventListener('openCookieSettings', handleOpenCookieSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenCookieSettings);
  }, []);

  const isImageFile = (file) => (
    file.type?.startsWith('image/') ||
    file.name.toLowerCase().endsWith('.png') ||
    file.name.toLowerCase().endsWith('.jpg') ||
    file.name.toLowerCase().endsWith('.jpeg') ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif')
  );

  const isPdfFile = (file) => (
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  );

  const handleFileAdd = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      const validFiles = filesArray.filter(file => validateFile(file));
      if (validFiles.length === 0) return;
      setFiles(prev => [...prev, ...validFiles]);
      e.target.value = '';
    }
  };

  const handleFileRemove = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFormValid = () =>
    email.trim() !== '' && validateEmail(email) && consentToTou && consentToPp;

  const validateFile = (file) => {
    const maxSize = 100 * 1024 * 1024;
    const totalSize = files.reduce((sum, f) => sum + f.size, 0) + file.size;

    if (totalSize > maxSize) {
      alert('Die Gesamtgröße aller Dateien darf 100MB nicht überschreiten. Bitte entfernen Sie einige Dateien oder wählen Sie kleinere Dateien aus.');
      return false;
    }

    return true;
  };

  // Helper function to load image from file
  const loadImageFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Convert a single image file to PDF
  const convertImageToPdf = async (imageFile) => {
    try {
      // First compress the image if needed
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg'
      };
      
      let processedImage = imageFile;
      if (imageFile.type.startsWith('image/') || 
          imageFile.name.toLowerCase().endsWith('.heic') || 
          imageFile.name.toLowerCase().endsWith('.heif')) {
        try {
          processedImage = await imageCompression(imageFile, options);
        } catch (error) {
          console.error('Error compressing image:', error);
          // Continue with original file if compression fails
        }
      }

      // Load the image
      const img = await loadImageFromFile(processedImage);
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: img.width > img.height ? 'landscape' : 'portrait',
        unit: 'mm'
      });

      // Calculate dimensions to fit the page while maintaining aspect ratio
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgAspectRatio = img.width / img.height;
      const pdfAspectRatio = pdfWidth / pdfHeight;

      let finalWidth, finalHeight;
      if (imgAspectRatio > pdfAspectRatio) {
        // Image is wider than PDF page
        finalWidth = pdfWidth;
        finalHeight = pdfWidth / imgAspectRatio;
      } else {
        // Image is taller than PDF page
        finalHeight = pdfHeight;
        finalWidth = pdfHeight * imgAspectRatio;
      }

      // Center the image on the page
      const x = (pdfWidth - finalWidth) / 2;
      const y = (pdfHeight - finalHeight) / 2;

      // Add image to PDF
      pdf.addImage(img, 'JPEG', x, y, finalWidth, finalHeight);

      // Convert PDF to blob and then to File
      const pdfBlob = pdf.output('blob');
      const fileName = imageFile.name.replace(/\.[^/.]+$/, '') + '.pdf';
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      return pdfFile;
    } catch (error) {
      console.error('Error converting image to PDF:', error);
      throw error;
    }
  };

  // Combine multiple image files into a single PDF
  const combineImagesToPdf = async (imageFiles) => {
    try {
      if (imageFiles.length === 0) {
        throw new Error('No images to combine');
      }

      if (imageFiles.length === 1) {
        // If only one image, just convert it to PDF
        return await convertImageToPdf(imageFiles[0]);
      }

      // Process all images first (compress if needed)
      const processedImages = await Promise.all(
        imageFiles.map(async (file) => {
          if (file.type.startsWith('image/') || 
              file.name.toLowerCase().endsWith('.heic') || 
              file.name.toLowerCase().endsWith('.heif')) {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              fileType: 'image/jpeg'
            };
            try {
              return await imageCompression(file, options);
            } catch (error) {
              console.error('Error compressing image:', error);
              return file;
            }
          }
          return file;
        })
      );

      // Load all images
      const images = await Promise.all(
        processedImages.map(file => loadImageFromFile(file))
      );

      // Create PDF
      const pdf = new jsPDF();
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Add each image as a new page
      images.forEach((img, index) => {
        if (index > 0) {
          pdf.addPage();
        }

        // Calculate dimensions to fit the page while maintaining aspect ratio
        const imgAspectRatio = img.width / img.height;
        const pdfAspectRatio = pdfWidth / pdfHeight;

        let finalWidth, finalHeight;
        if (imgAspectRatio > pdfAspectRatio) {
          finalWidth = pdfWidth;
          finalHeight = pdfWidth / imgAspectRatio;
        } else {
          finalHeight = pdfHeight;
          finalWidth = pdfHeight * imgAspectRatio;
        }

        // Center the image on the page
        const x = (pdfWidth - finalWidth) / 2;
        const y = (pdfHeight - finalHeight) / 2;

        // Add image to PDF
        pdf.addImage(img, 'JPEG', x, y, finalWidth, finalHeight);
      });

      // Convert PDF to blob and then to File
      const pdfBlob = pdf.output('blob');
      const baseFileName = imageFiles[0].name.replace(/\.[^/.]+$/, '');
      const fileName = baseFileName + '_combined.pdf';
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      return pdfFile;
    } catch (error) {
      console.error('Error combining images to PDF:', error);
      throw error;
    }
  };

  // no explicit content-type inference needed; rely on file.type when available

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files;
    if (dropped.length > 0) {
      const validFiles = Array.from(dropped).filter(file => validateFile(file));
      if (validFiles.length > 0) {
        setFiles(prev => [...prev, ...validFiles]);
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const CheckmarkIcon = () => (
    <svg 
      className="checkmark-icon" 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path 
        d="M20 6L9 17L4 12" 
        stroke="#16A34A" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  );

  const prepareDocumentFiles = async (files) => {
    const imageFiles = files.filter(isImageFile);
    const pdfFiles = files.filter(isPdfFile);
    const preparedFiles = [];

    if (imageFiles.length > 0) {
      preparedFiles.push(await combineImagesToPdf(imageFiles));
    }

    preparedFiles.push(...pdfFiles);
    return preparedFiles;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;
    setIsSubmitting(true);
    setUploadProgress(0);

    progressIntervalRef.current = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 99) {
          clearInterval(progressIntervalRef.current);
          return 99;
        }
        return prev + 2;
      });
    }, 100);

    try {
      const processedFiles = files.length > 0 ? await prepareDocumentFiles(files) : [];
      const allFiles = processedFiles.map(f => ({ file: f, label: f.name.replace(/\.[^/.]+$/, '') }));

      // Upload every file to S3 via a Brunhilde presigned URL first; the submission
      // then references the S3 filenames Brunhilde generated.
      const uploadedFiles = [];
      for (const { file, label } of allFiles) {
        const contentType = file.type || 'application/octet-stream';
        const uploadUrlResponse = await axios.get(`${API_BASE_URL}/files/upload_url`, {
          params: {
            original_filename: file.name,
            content_type: contentType,
          },
          headers: {
            Authorization: `Bearer ${API_KEY}`,
          },
        });
        const { presigned_upload_url, s3_filename } = uploadUrlResponse.data;
        await axios.put(presigned_upload_url, file, {
          headers: { 'Content-Type': contentType },
        });
        uploadedFiles.push({ filename: s3_filename, label });
      }

      const attribution = hasMarketingConsent()
        ? {
            marketing_consent: true,
            source_url: window.location.href,
            ...getOpenAiAttributionCookies(),
          }
        : undefined;

      const submissionResponse = await axios.post(
        `${API_BASE_URL}/document_submissions`,
        {
          submission_type: 'COMM_UTCS_SUB',
          files: uploadedFiles,
          payload_text: extraInfo.trim(),
          submitter_name: submitterName.trim() || undefined,
          submitter_email: email,
          submitter_phone: submitterPhone.trim() || undefined,
          submitter_company: submitterCompany.trim() || undefined,
          agrees_to_data_processing: consentToPp,
          marketing_opt_in: consentToEmailMarketing,
          ...(attribution ? { attribution } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${API_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Same event_id as the server-side Conversions API event → deduplicated by OpenAI.
      const submissionId = submissionResponse.data?.id;
      if (hasMarketingConsent() && submissionId) {
        measureLeadCreated(submissionId);
      }

      clearInterval(progressIntervalRef.current);
      setUploadProgress(100);
      setSubmissionStatus('success');
    } catch (error) {
      console.error('Submission error:', error);
      clearInterval(progressIntervalRef.current);
      if (error.response?.status === 422) {
        alert('Ungültige Eingabe. Bitte überprüfen Sie Ihre E-Mail-Adresse und die hochgeladenen Dateien.');
      } else if (error.response?.status === 502 || error.response?.status === 504) {
        alert('Der Server ist vorübergehend nicht erreichbar. Bitte versuchen Sie es erneut.');
      } else if (error.request) {
        alert('Keine Antwort vom Server erhalten. Bitte überprüfen Sie Ihre Internetverbindung.');
      } else {
        alert('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
      setSubmissionStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    window.location.reload();
  };

  const handleCookieBannerAccept = () => setShowCookieBanner(false);
  const handleCookieBannerShowSettings = () => { setShowCookieBanner(false); setShowCookieSettings(true); };
  const handleCookieSettingsClose = () => setShowCookieSettings(false);

  if (submissionStatus === 'success') {
    return (
      <div className="App" style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div className="upload-container" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          textAlign: 'center',
          padding: '40px',
          maxWidth: '600px'
        }}>
          <img src={checkMarkIcon} alt="Success" style={{ width: '60px', height: '60px', marginBottom: '30px' }} />
          <h1 style={{ marginBottom: '20px' }}>Einreichung erfolgreich!</h1>
          <p style={{ marginBottom: '20px' }}>Vielen Dank für Ihre Anfrage, sie erhalten eine E-Mail von uns.</p>
        </div>
      </div>
    );
  }

  if (submissionStatus === 'error') {
    return (
      <div className="App" style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div className="upload-container" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          textAlign: 'center',
          padding: '40px',
          maxWidth: '600px'
        }}>
          <h1 style={{ marginBottom: '20px' }}>Einreichung fehlgeschlagen</h1>
          <p style={{ 
            marginBottom: '30px',
            fontSize: '18px',
            color: '#666',
            maxWidth: '500px',
            lineHeight: '1.5'
          }}>
            Bei der Einreichung Ihrer Dokumente ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
          </p>
          <button 
            className="submit-button" 
            onClick={handleReset}
            style={{
              padding: '12px 40px',
              height: 'auto',
              minHeight: 'auto',
              width: 'auto',
              flex: 'none'
            }}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className="App" style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div className="upload-container" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          textAlign: 'center',
          padding: '40px',
          maxWidth: '600px'
        }}>
          <UploadingIndicator size={84} />
          <h1 style={{ marginBottom: '20px' }}>Anfrage wird gesendet...</h1>
          <div className="progress-bar-container">
            <div 
              className="progress-bar" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p style={{ marginTop: '20px', color: '#666' }}>
            {uploadProgress}% abgeschlossen
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="upload-container">

        {/* Centered logo header */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
          <img src={Logo} alt="Nebenkostenprofi Logo" style={{ width: '36px', height: '36px' }} />
          <span style={{ fontWeight: 700, fontSize: '1rem', color: '#111827', letterSpacing: '-0.02em' }}>
            Nebenkostenprofi
          </span>
        </div>

        <div className="upload-stage active">
          <div className="upload-box" style={{ maxWidth: '560px' }}>

            <h2 style={{ marginBottom: '1.25rem' }}>Ihre Anfrage</h2>

            {/* Contact fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', width: '100%', textAlign: 'left' }}>
              <div>
                <label className="form-label">Name <span style={{ color: '#9CA3AF' }}>(optional)</span></label>
                <input type="text" placeholder="Max Mustermann" className="email-field"
                  value={submitterName} onChange={(e) => setSubmitterName(e.target.value)} />
              </div>
              <div>
                <label className="form-label">Unternehmen <span style={{ color: '#9CA3AF' }}>(optional)</span></label>
                <input type="text" placeholder="Muster GmbH" className="email-field"
                  value={submitterCompany} onChange={(e) => setSubmitterCompany(e.target.value)} />
              </div>
              <div>
                <label className="form-label">
                  E-Mail-Adresse
                  {(!email.trim() || !validateEmail(email)) && <span className="required-badge">*</span>}
                </label>
                <input type="email" placeholder="max@beispiel.de" className="email-field"
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <label className="form-label">Telefonnummer <span style={{ color: '#9CA3AF' }}>(optional)</span></label>
                <input type="tel" placeholder="+49 40 12345678" className="email-field"
                  value={submitterPhone} onChange={(e) => setSubmitterPhone(e.target.value)} />
              </div>
            </div>

            {/* Message */}
            <div style={{ width: '100%', marginTop: '0.75rem', textAlign: 'left' }}>
              <label className="form-label">Nachricht <span style={{ color: '#9CA3AF' }}>(optional)</span></label>
              <textarea
                placeholder="Beschreiben Sie Ihr Anliegen..."
                className="email-field"
                value={extraInfo}
                onChange={(e) => setExtraInfo(e.target.value)}
                rows={4}
                style={{ resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            {/* Subtle file drop zone */}
            <div
              style={{ width: '100%', marginTop: '0.5rem' }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="*/*"
                style={{ display: 'none' }}
                onChange={handleFileAdd}
              />
              <button
                type="button"
                onClick={handleUploadClick}
                className={`file-drop-zone${isDragging ? ' file-drop-zone--dragging' : ''}`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Dateien anhängen</span>
              </button>

              {files.length > 0 && (
                <ul style={{ listStyle: 'none', margin: '0.5rem 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {files.map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#374151' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: '0 2px', lineHeight: 1, fontSize: '1rem' }}
                        aria-label="Entfernen"
                      >×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Consents */}
            <div className="consents" style={{ width: '100%', marginTop: '1rem', textAlign: 'left' }}>
              <label className="consent-item">
                <input type="checkbox" checked={consentToPp} onChange={(e) => setConsentToPp(e.target.checked)} />
                <span>
                  Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
                  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a> zu
                </span>
                {!consentToPp && <span className="required-badge">*</span>}
              </label>
              <label className="consent-item">
                <input type="checkbox" checked={consentToTou} onChange={(e) => setConsentToTou(e.target.checked)} />
                <span>
                  Ich akzeptiere die{' '}
                  <a href={AGB_URL} target="_blank" rel="noopener noreferrer">AGB</a>
                </span>
                {!consentToTou && <span className="required-badge">*</span>}
              </label>
              <label className="consent-item optional">
                <input type="checkbox" checked={consentToEmailMarketing} onChange={(e) => setConsentToEmailMarketing(e.target.checked)} />
                <span>Newsletter & Angebote erhalten (optional)</span>
              </label>
            </div>

            {/* Submit */}
            <div className="navigation-buttons" style={{ marginTop: '1.25rem' }}>
              <button
                className="submit-button"
                disabled={!isFormValid() || isSubmitting}
                onClick={handleSubmit}
                style={{ flex: 1 }}
              >
                {isSubmitting ? 'Wird gesendet...' : 'Anfrage absenden'}
              </button>
            </div>

          </div>
        </div>

      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        <button
          onClick={() => {
            const event = new CustomEvent('openCookieSettings');
            window.dispatchEvent(event);
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#4b5563',
            fontSize: 12,
            padding: 0,
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Cookie-Einstellungen
        </button>
      </div>
      {showCookieBanner && (
        <CookieBanner onAccept={handleCookieBannerAccept} onShowSettings={handleCookieBannerShowSettings} />
      )}
      <CookieSettingsModal open={showCookieSettings} onClose={handleCookieSettingsClose} />
    </div>
  );
}

export default App;
